
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Users, Ban, Check, Edit, Trash2, Loader2 } from 'lucide-react';
import { API } from '../../utils/api';

export function AdminPlans({ headers }) {
  const [plans, setPlans] = useState([]);
  const [totalParents, setTotalParents] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    duration_days: 30,
    max_transactions: -1,
    max_children: -1,
    max_events: 0,
    max_event_line_items: 0,
    max_divisions: -1,
    max_org_seats: -1,
    max_swish_accounts: -1,
    cost_monthly: 0,
    cost_annual: 0,
    currency: 'SEK',
    plan_audience: 'all',
    is_default: false,
    is_enabled: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/admin/plans`, { headers });
      setPlans(res.data.items || []);
      setTotalParents(res.data.total_parents || 0);
    } catch (e) {
      toast.error('Failed to fetch plans');
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const filteredPlans = plans.filter(p => 
    (p.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExport = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Name,Description,Duration,Max Txns,Monthly Cost,Annual Cost,Currency,Default,Enabled,Subscribers\n"
      + filteredPlans.map(p => `"${p.name}","${(p.description || '').replace(/"/g, '""')}",${p.duration_days},${p.max_transactions},${p.cost_monthly},${p.cost_annual},${p.currency},${p.is_default},${p.is_enabled},${p.subscriber_count}`).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "subscription_plans.csv");
    document.body.appendChild(link);
    link.click();
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      duration_days: 30,
      max_transactions: -1,
      max_children: -1,
      max_events: 0,
      max_event_line_items: 0,
      max_divisions: -1,
      max_org_seats: -1,
      max_swish_accounts: -1,
      cost_monthly: 0,
      cost_annual: 0,
      currency: 'SEK',
      plan_audience: 'all',
      is_default: false,
      is_enabled: true
    });
    setEditingId(null);
    setShowCreate(false);
  };

  const startEdit = (plan) => {
    setForm({
      name: plan.name,
      description: plan.description || '',
      duration_days: plan.duration_days || 30,
      max_transactions: plan.max_transactions ?? -1,
      max_children: plan.max_children ?? -1,
      max_events: plan.max_events ?? 0,
      max_event_line_items: plan.max_event_line_items ?? 0,
      max_divisions: plan.max_divisions ?? -1,
      max_org_seats: plan.max_org_seats ?? -1,
      max_swish_accounts: plan.max_swish_accounts ?? -1,
      cost_monthly: plan.cost_monthly || plan.cost || 0,
      cost_annual: plan.cost_annual || 0,
      currency: plan.currency || 'SEK',
      plan_audience: plan.plan_audience || 'all',
      is_default: plan.is_default || false,
      is_enabled: plan.is_enabled !== false
    });
    setEditingId(plan.id);
    setShowCreate(true);
  };

  const handleSubmit = async () => {
    if (saving) return;
    setSaving(true);
    // Guard: never send NaN/empty numeric caps (would silently wipe a limit)
    const numericKeys = ['max_transactions', 'max_children', 'max_events', 'max_event_line_items', 'max_divisions', 'max_org_seats', 'max_swish_accounts'];
    const payload = { ...form };
    numericKeys.forEach((k) => { if (payload[k] === '' || payload[k] === null || Number.isNaN(payload[k])) delete payload[k]; });
    try {
      if (editingId) {
        await axios.put(`${API}/admin/plans/${editingId}`, payload, { headers });
        toast.success('Plan updated');
      } else {
        await axios.post(`${API}/admin/plans`, payload, { headers });
        toast.success('Plan created');
      }
      resetForm();
      fetchPlans();
    } catch (e) {
      toast.error(editingId ? 'Failed to update plan' : 'Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plan? Existing subscribers will remain until their term ends.')) return;
    try {
      await axios.delete(`${API}/admin/plans/${id}`, { headers });
      toast.success('Plan deleted');
      fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const toggleEnabled = async (plan) => {
    try {
      await axios.put(`${API}/admin/plans/${plan.id}`, { is_enabled: !plan.is_enabled }, { headers });
      toast.success(plan.is_enabled ? 'Plan disabled - no new subscribers accepted' : 'Plan enabled');
      fetchPlans();
    } catch (e) {
      toast.error('Failed to update plan');
    }
  };

  // Calculate annual savings percentage
  const calcSavings = (monthly, annual) => {
    if (!monthly || monthly <= 0) return 0;
    const yearlyFromMonthly = monthly * 12;
    if (yearlyFromMonthly <= 0 || annual <= 0) return 0;
    return Math.round(((yearlyFromMonthly - annual) / yearlyFromMonthly) * 100);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Subscription Plans</h2>
          <p className="text-sm text-muted-foreground">Total payer accounts: {totalParents}</p>
        </div>
        <div className="flex gap-2">
          <Input 
            placeholder="Search plans..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <Button variant="outline" onClick={handleExport}>Export CSV</Button>
          {!showCreate && <Button onClick={() => setShowCreate(true)}>+ Add Plan</Button>}
        </div>
      </div>

      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Plan' : 'Add New Plan'}</CardTitle>
            <CardDescription>Configure subscription plan details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan Name</Label>
                <Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} placeholder="e.g., Premium Family" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={v=>setForm({...form, currency: v})}>
                  <SelectTrigger><SelectValue placeholder="Select Currency" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SEK">SEK</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Description (supports emojis 🎉)</Label>
              <Textarea 
                value={form.description} 
                onChange={e=>setForm({...form, description: e.target.value})} 
                placeholder="Enter plan description... 
Use multiple lines and emojis! 🚀
• Feature 1
• Feature 2"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Cost ({form.currency})</Label>
                <Input type="number" value={form.cost_monthly} onChange={e=>setForm({...form, cost_monthly: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Annual Cost ({form.currency}) {form.cost_monthly > 0 && form.cost_annual > 0 && (
                  <span className="text-green-600 text-xs ml-2">
                    Save {calcSavings(form.cost_monthly, form.cost_annual)}%
                  </span>
                )}</Label>
                <Input type="number" value={form.cost_annual} onChange={e=>setForm({...form, cost_annual: parseFloat(e.target.value) || 0})} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Duration (Days)</Label>
                <Input type="number" value={form.duration_days} onChange={e=>setForm({...form, duration_days: parseInt(e.target.value) || 30})} />
              </div>
              <div>
                <Label>Max Transactions (-1 = unlimited)</Label>
                <Input type="number" value={form.max_transactions} onChange={e=>setForm({...form, max_transactions: e.target.value === '' ? '' : parseInt(e.target.value)})} />
              </div>
              <div>
                <Label>Max Children (-1 = unlimited)</Label>
                <Input type="number" value={form.max_children} onChange={e=>setForm({...form, max_children: e.target.value === '' ? '' : parseInt(e.target.value)})} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max Personal Events (0 = off, -1 = unlimited)</Label>
                <Input type="number" value={form.max_events} onChange={e=>setForm({...form, max_events: e.target.value === '' ? '' : parseInt(e.target.value)})} data-testid="plan-max-events-input" />
              </div>
              <div>
                <Label>Event Line Items (0-4, 0 = amount only)</Label>
                <Input type="number" min={0} max={4} value={form.max_event_line_items} onChange={e=>setForm({...form, max_event_line_items: e.target.value === '' ? '' : parseInt(e.target.value)})} data-testid="plan-max-line-items-input" />
              </div>
            </div>

            <div className="pt-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Business / Organisation (ERP) limits</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Max Divisions (-1 = unlimited)</Label>
                  <Input type="number" value={form.max_divisions} onChange={e=>setForm({...form, max_divisions: e.target.value === '' ? '' : parseInt(e.target.value)})} data-testid="plan-max-divisions-input" />
                </div>
                <div>
                  <Label>Max Org Seats (-1 = unlimited)</Label>
                  <Input type="number" value={form.max_org_seats} onChange={e=>setForm({...form, max_org_seats: e.target.value === '' ? '' : parseInt(e.target.value)})} data-testid="plan-max-seats-input" />
                </div>
                <div>
                  <Label>Max Swish Accounts (-1 = unlimited)</Label>
                  <Input type="number" value={form.max_swish_accounts} onChange={e=>setForm({...form, max_swish_accounts: e.target.value === '' ? '' : parseInt(e.target.value)})} data-testid="plan-max-swish-input" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan Audience</Label>
                <Select value={form.plan_audience} onValueChange={v=>setForm({...form, plan_audience: v})}>
                  <SelectTrigger data-testid="plan-audience-select"><SelectValue placeholder="Audience" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Everyone (personal + business)</SelectItem>
                    <SelectItem value="personal">Personal only</SelectItem>
                    <SelectItem value="business">Business / Enterprise only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_default} onCheckedChange={c=>setForm({...form, is_default: c})} />
                <Label>Default Plan</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_enabled} onCheckedChange={c=>setForm({...form, is_enabled: c})} />
                <Label>Accepting New Subscribers</Label>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={saving} data-testid="plan-save-button">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {saving ? 'Saving…' : (editingId ? 'Update Plan' : 'Create Plan')}
              </Button>
              <Button variant="secondary" onClick={resetForm} disabled={saving}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {filteredPlans.map(p => {
          const savings = calcSavings(p.cost_monthly, p.cost_annual);
          
          return (
            <Card key={p.id} className={`${p.is_default ? 'border-blue-500 border-2' : ''} ${!p.is_enabled ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-lg">{p.name}</span>
                      {p.is_default && <Badge variant="default">Default</Badge>}
                      {!p.is_enabled && <Badge variant="destructive"><Ban className="h-3 w-3 mr-1" />Closed</Badge>}
                      {p.is_enabled && <Badge variant="outline" className="text-green-600"><Check className="h-3 w-3 mr-1" />Open</Badge>}
                    </div>
                    
                    {p.description && (
                      <div className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{p.description}</div>
                    )}
                    
                    <div className="flex flex-wrap gap-4 mt-3 text-sm">
                      <div className="bg-gray-100 px-3 py-1 rounded">
                        <span className="text-muted-foreground">Monthly:</span> <strong>{p.cost_monthly} {p.currency}</strong>
                      </div>
                      <div className="bg-gray-100 px-3 py-1 rounded">
                        <span className="text-muted-foreground">Annual:</span> <strong>{p.cost_annual} {p.currency}</strong>
                        {savings > 0 && <span className="text-green-600 ml-1">(-{savings}%)</span>}
                      </div>
                      <div className="bg-gray-100 px-3 py-1 rounded">
                        <span className="text-muted-foreground">Txns:</span> <strong>{p.max_transactions === -1 ? '∞' : p.max_transactions}</strong>
                      </div>
                      <div className="bg-gray-100 px-3 py-1 rounded">
                        <span className="text-muted-foreground">Events:</span> <strong>{p.max_events === -1 ? '∞' : (p.max_events ?? 0)}</strong>
                        <span className="text-muted-foreground ml-1">/ Items:</span> <strong>{p.max_event_line_items ?? 0}</strong>
                      </div>
                      <div className="bg-gray-100 px-3 py-1 rounded">
                        <span className="text-muted-foreground">Divisions:</span> <strong>{p.max_divisions === -1 || p.max_divisions == null ? '∞' : p.max_divisions}</strong>
                        <span className="text-muted-foreground mx-1">·</span>
                        <span className="text-muted-foreground">Seats:</span> <strong>{p.max_org_seats === -1 || p.max_org_seats == null ? '∞' : p.max_org_seats}</strong>
                        <span className="text-muted-foreground mx-1">·</span>
                        <span className="text-muted-foreground">Swish:</span> <strong>{p.max_swish_accounts === -1 || p.max_swish_accounts == null ? '∞' : p.max_swish_accounts}</strong>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-3 text-sm">
                      <Users className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">{p.subscriber_count || 0}</span>
                      <span className="text-muted-foreground">/ {p.total_parents || totalParents} subscribers</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    <Button variant="outline" size="sm" onClick={() => startEdit(p)}>
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <Button 
                      variant={p.is_enabled ? "secondary" : "default"} 
                      size="sm" 
                      onClick={() => toggleEnabled(p)}
                    >
                      {p.is_enabled ? <><Ban className="h-4 w-4 mr-1" /> Disable</> : <><Check className="h-4 w-4 mr-1" /> Enable</>}
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(p.id)}>
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        
        {filteredPlans.length === 0 && (
          <div className="text-center py-10 text-muted-foreground">
            No subscription plans found. Create one to get started.
          </div>
        )}
      </div>
    </div>
  );
}
